import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopAIStoragePage.css';
import useLanguage from '../../../hooks/useLanguage';
import { buildMediaCounters } from '../../Mobile/MobileAIStorage/utils';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { Skeleton } from '../../effects';
import DesktopAIStorageDetailPage, { AlbumModal } from './DesktopAIStorageDetailPage';
import aiStorageService from '../../../services/AIStorageService';

import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconAIFolder from '../../../assets/images/icons/icon-ai-folder.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';

const STORAGE_LIMIT_BYTES = 20 * 1024 * 1024 * 1024;

const formatStoragePercentage = (usedBytes, limitBytes) => {
  if (!limitBytes || Number.isNaN(limitBytes) || limitBytes <= 0) {
    return '0%';
  }

  const ratio = usedBytes && usedBytes > 0 ? (usedBytes / limitBytes) * 100 : 0;
  const safeRatio = Math.min(100, Math.max(0, ratio));
  const rounded = safeRatio >= 10 ? Math.round(safeRatio) : Math.round(safeRatio * 10) / 10;
  return `${rounded}%`;
};

const StorageStatCard = ({ label, value, hint, icon }) => (
  <div className="loopino-ai-stat-shell">
    <div className="loopino-ai-stat-card loopino-card">
      <div className="loopino-ai-stat-icon">
        {icon}
      </div>
      <div className="loopino-ai-stat-content">
        <p className="loopino-ai-stat-label">{label}</p>
        <p className="loopino-ai-stat-value">{value}</p>
        {hint && <p className="loopino-ai-stat-hint">{hint}</p>}
      </div>
    </div>
  </div>
);

const ProjectCard = ({
  project,
  formatProjectDate,
  onOpen,
  onDelete,
  t,
  getImageLoadingProps,
}) => {
  const previewFiles = project.files?.slice(0, 6) || [];
  const hasMoreFiles = project.filesCount > previewFiles.length;
  const extraFilesCount = hasMoreFiles ? project.filesCount - previewFiles.length : 0;
  const lastPreviewIndex = previewFiles.length - 1;
  const handleCardOpen = () => onOpen(project);
  const counters = [];
  if (project.imagesCount > 0) {
    counters.push({
      icon: iconImage,
      label: project.imagesCount,
    });
  }
  if (project.videosCount > 0) {
    counters.push({
      icon: iconVideo,
      label: project.videosCount,
    });
  }
  if (counters.length === 0 && project.filesCount > 0) {
    counters.push({
      icon: iconImage,
      label: project.filesCount,
    });
  }

  const fallbackTitles = ['Album senza titolo', 'Untitled album'];
  const displayTitle = project.title && !fallbackTitles.includes(project.title.trim())
    ? project.title
    : t('aiStorage.list.defaults.untitledAlbum');

  return (
    <div
      className="loopino-ai-project-card loopino-card"
      role="button"
      tabIndex={0}
      onClick={handleCardOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleCardOpen();
        }
      }}
    >
      <div className="loopino-ai-project-card-header">
        <div>
          <p className="loopino-ai-project-eyebrow">{t('aiStorage.header.title', { defaultValue: 'AI Storage' })}</p>
          <h3 className="loopino-ai-project-title">{displayTitle}</h3>
        </div>
        <div className="loopino-ai-project-actions">
          <button
            type="button"
            className="loopino-button loopino-ai-button-compact"
            data-variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(project);
            }}
          >
            {t('aiStorage.actions.openShort', { defaultValue: 'Apri' })}
          </button>
          <button
            type="button"
            className="loopino-button loopino-ai-button-compact loopino-ai-delete-button"
            data-variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(project);
            }}
            aria-label={t('aiStorage.list.accessibility.deleteProject')}
          >
            <span aria-hidden className="loopino-ai-delete-icon">×</span>
          </button>
        </div>
      </div>

      {previewFiles.length > 0 && (
        <div className="loopino-ai-project-thumbs">
          {previewFiles.map((file, index) => {
            const mediaKey = file?.id ? `media-${file.id}` : `${file?.url || index}`;
            const loadingProps = getImageLoadingProps(mediaKey, `album-${project.id}`);
            const isImage = file.type === 'image';
            const isVideo = file.type === 'video';
            const source = file.url || file.thumbnailUrl || placeholderImage;
            const showSkeleton = loadingProps && loadingProps.status !== 'loaded';
            const shouldShowMoreOverlay = extraFilesCount > 0 && index === lastPreviewIndex;

            return (
              <div key={mediaKey} className="loopino-ai-thumb">
                {isImage ? (
                  <>
                    <img
                      src={source}
                      alt={file.name}
                      className={['loopino-ai-thumb-image', loadingProps?.className || ''].filter(Boolean).join(' ')}
                      loading="lazy"
                      decoding="async"
                      onLoad={loadingProps?.onLoad}
                      onError={(event) => {
                        loadingProps?.onError?.(event);
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = placeholderImage;
                      }}
                    />
                    {showSkeleton && (
                      <div className="loopino-ai-thumb-skeleton" aria-hidden>
                        <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                      </div>
                    )}
                  </>
                ) : isVideo ? (
                  <div className="loopino-ai-thumb-video">
                    <video src={source} muted preload="metadata" />
                    <div className="loopino-ai-thumb-video-overlay">
                      <img src={iconVideo} alt={t('aiStorage.list.alt.video')} />
                    </div>
                  </div>
                ) : (
                  <div className="loopino-ai-thumb-placeholder">
                    <img src={iconImage} alt={t('aiStorage.list.alt.media')} />
                  </div>
                )}
                {shouldShowMoreOverlay && (
                  <div className="loopino-ai-thumb-more">+{extraFilesCount}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="loopino-ai-project-footer">
        <div className="loopino-ai-project-counts">
          {counters.map((counter, idx) => (
            <span key={`${counter.icon}-${idx}`} className="loopino-ai-counter">
              <img src={counter.icon} alt="" />
              {counter.label}
            </span>
          ))}
        </div>
        <div className="loopino-ai-project-date">
          {formatProjectDate(project.updatedAt)}
        </div>
      </div>
    </div>
  );
};

const DesktopAIStoragePageInner = () => {
  const { t, language } = useLanguage();
  const { confirm } = useDesktopAlert();

  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [imageLoadingStates, setImageLoadingStates] = useState(() => new Map());

  const projectDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (err) {
      console.warn('[DesktopAIStorage] Falling back to default date formatter', err);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
  }, [language]);

  const formatProjectDate = useCallback((date) => {
    if (!date) {
      return '--';
    }

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return '--';
    }

    return projectDateFormatter.format(parsedDate);
  }, [projectDateFormatter]);

  const setImageLoadingState = useCallback((key, status) => {
    if (!key) {
      return;
    }

    setImageLoadingStates((prev) => {
      const current = prev.get(key);
      if (current === status) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, []);

  const getImageLoadingProps = useCallback((source, scope = 'thumbnail') => {
    const normalizedScope = scope || 'thumbnail';
    const baseKey = typeof source === 'string' && source.length > 0
      ? source
      : 'unknown';
    const mapKey = `${normalizedScope}::${baseKey}`;
    const status = imageLoadingStates.get(mapKey) || 'loading';

    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageLoadingState(mapKey, 'loaded'),
      onError: () => setImageLoadingState(mapKey, 'error'),
    };
  }, [imageLoadingStates, setImageLoadingState]);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const normalizedProjects = await aiStorageService.getProjects({
        page: 1,
        pageSize: 50,
        includeArchived: false,
        previewLimit: 6,
      });
      setProjects(normalizedProjects);
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to load projects', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.list.errors.load', { error: message }));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleProjectCreated = useCallback((newProject) => {
    setShowCreateModal(false);

    if (!newProject) {
      loadProjects();
      return;
    }

    setProjects((prev) => {
      const filtered = prev.filter((project) => project.id !== newProject.id);
      return [newProject, ...filtered];
    });

    setCurrentProjectId(newProject.id);
  }, [loadProjects]);

  const handleDeleteProject = useCallback(async (project) => {
    const confirmed = await confirm({
      title: t('aiStorage.messages.deleteProjectConfirm'),
      variant: 'warning',
      confirmText: t('aiStorage.actions.delete', { defaultValue: 'Elimina' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' }),
    });
    if (!confirmed) return;

    try {
      await aiStorageService.deleteProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to delete album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.list.errors.delete', { error: message }));
    }
  }, [confirm, t]);

  const handleProjectUpdated = useCallback((updatedProject) => {
    if (!updatedProject?.id) return;
    setProjects((prev) => prev.map((project) => (
      project.id === updatedProject.id
        ? { ...project, ...updatedProject }
        : project
    )));
  }, []);

  const handleProjectDeleted = useCallback((projectId) => {
    setProjects((prev) => prev.filter((project) => project.id !== projectId));
    setCurrentProjectId(null);
  }, []);

  const totals = useMemo(() => {
    const computeProjectSizeBytes = (project) => {
      const sizeFromAlbum = typeof project.currentTotalSizeKb === 'number'
        ? project.currentTotalSizeKb * 1024
        : null;

      if (sizeFromAlbum != null) {
        return sizeFromAlbum;
      }

      if (Array.isArray(project.files)) {
        return project.files.reduce((sum, file) => {
          const fileSize = typeof file?.sizeBytes === 'number' ? file.sizeBytes : 0;
          return sum + fileSize;
        }, 0);
      }

      return 0;
    };

    const aggregate = projects.reduce((acc, project) => {
      acc.images += project.imagesCount || 0;
      acc.videos += project.videosCount || 0;
      acc.files += project.filesCount || project.mediaCount || 0;
      acc.storageUsedBytes += computeProjectSizeBytes(project);
      return acc;
    }, { images: 0, videos: 0, files: 0, storageUsedBytes: 0 });

    return {
      projects: projects.length,
      ...aggregate,
    };
  }, [projects]);

  const renderEmptyState = () => (
    <div className="loopino-ai-empty loopino-glass-panel">
      <div className="loopino-ai-empty-icon">
        <img src={iconAi} alt={t('aiStorage.list.alt.storage')} />
      </div>
      <h3 className="loopino-ai-empty-title">{t('aiStorage.list.empty.title')}</h3>
      <p className="loopino-ai-empty-description">
        {t('aiStorage.list.empty.description')}
      </p>
      <button
        type="button"
        className="loopino-button"
        data-variant="primary"
        onClick={() => setShowCreateModal(true)}
      >
        {t('aiStorage.list.empty.cta')}
      </button>
    </div>
  );

  const renderLoadingSkeleton = () => {
    const skeletonItems = Array.from({ length: 3 });
    return (
      <div className="loopino-ai-projects-grid">
        {skeletonItems.map((_, index) => (
          <div key={`skeleton-${index}`} className="loopino-ai-project-card loopino-card is-skeleton">
            <div className="loopino-ai-project-card-header">
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton width="70%" height={22} shimmer />
                <Skeleton width="50%" height={14} shimmer />
              </div>
              <Skeleton width={120} height={36} shimmer />
            </div>
            <div className="loopino-ai-project-thumbs">
              {Array.from({ length: 5 }).map((__, thumbIndex) => (
                <div key={`thumb-${thumbIndex}`} className="loopino-ai-thumb">
                  <Skeleton width="100%" height="100%" variant="rounded" shimmer />
                </div>
              ))}
            </div>
            <div className="loopino-ai-project-footer">
              <Skeleton width={90} height={12} shimmer />
              <Skeleton width={70} height={12} shimmer />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (currentProjectId) {
    return (
      <DesktopAIStorageDetailPage
        projectId={currentProjectId}
        onBack={() => setCurrentProjectId(null)}
        onProjectUpdated={handleProjectUpdated}
        onProjectDeleted={handleProjectDeleted}
      />
    );
  }

  return (
    <div className="loopino-desktop-shell loopino-ai-storage-shell">
      <header className="loopino-desktop-post-review-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{t('aiStorage.header.title', { defaultValue: 'AI Storage' })}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('aiStorage.header.subtitle', {
              defaultValue: 'Organizza progetti, immagini e video generati dall\'AI. Gestisci album, descrizioni e spazio di archiviazione dalla vista desktop ridisegnata.',
            })}
          </p>
        </div>
        <div className="loopino-desktop-post-review-actions">
          <button
            type="button"
            className="loopino-button"
            data-variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <img src={iconAi} alt="" />
            {t('aiStorage.actions.createAlbum', { defaultValue: 'Crea nuovo album' })}
          </button>
        </div>
      </header>

      <div className="loopino-ai-stats">
        <StorageStatCard
          label={t('aiStorage.desktop.stats.projects', { defaultValue: 'Progetti' })}
          value={totals.projects}
          hint={t('aiStorage.desktop.stats.projectsHint', { defaultValue: 'Album attivi' })}
          icon={<img src={iconAIFolder} alt="" />}
        />
        <StorageStatCard
          label={t('aiStorage.desktop.stats.images', { defaultValue: 'Immagini' })}
          value={totals.images}
          hint={t('aiStorage.desktop.stats.assets', { defaultValue: 'File visivi' })}
          icon={<img src={iconImage} alt="" />}
        />
        <StorageStatCard
          label={t('aiStorage.desktop.stats.videos', { defaultValue: 'Video' })}
          value={totals.videos}
          hint={t('aiStorage.desktop.stats.media', { defaultValue: 'Clip generate' })}
          icon={<img src={iconVideo} alt="" />}
        />
        <StorageStatCard
          label={t('aiStorage.desktop.stats.storageUsed', { defaultValue: 'Spazio usato' })}
          value={formatStoragePercentage(totals.storageUsedBytes, STORAGE_LIMIT_BYTES)}
          hint={t('aiStorage.desktop.stats.storageLimit', { defaultValue: 'Capienza massima 20 GB' })}
          icon={<img src={iconAi} alt="" />}
        />
      </div>

      {error && (
        <div className="loopino-ai-banner error">
          {error}
          <button
            type="button"
            className="loopino-button"
            data-variant="ghost"
            onClick={loadProjects}
          >
            {t('aiStorage.actions.retry')}
          </button>
        </div>
      )}

      <div className="loopino-ai-projects-panel loopino-card">
        <div className="loopino-ai-projects-header">
          <div>
            <p className="loopino-ai-storage-eyebrow">{t('aiStorage.desktop.panelEyebrow', { defaultValue: 'Album AI' })}</p>
            <h3>{t('aiStorage.desktop.panelTitle', { defaultValue: 'Archivio progetti AI' })}</h3>
            <p className="loopino-ai-storage-summary">
              {t('aiStorage.desktop.panelSubtitle', { defaultValue: 'Anteprime veloci, conteggi e gestione immediata dei progetti AI.' })}
            </p>
          </div>
        </div>

        {isLoading ? (
          renderLoadingSkeleton()
        ) : projects.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className="loopino-ai-projects-grid">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                formatProjectDate={formatProjectDate}
                onOpen={(p) => setCurrentProjectId(p.id)}
                onDelete={handleDeleteProject}
                t={t}
                getImageLoadingProps={getImageLoadingProps}
              />
            ))}
          </div>
        )}
      </div>

      <AlbumModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCompleted={handleProjectCreated}
      />
    </div>
  );
};

const DesktopAIStoragePage = () => (
  <DesktopAlertProvider>
    <DesktopAIStoragePageInner />
  </DesktopAlertProvider>
);

export default DesktopAIStoragePage;
